import React, { useEffect, useState } from 'react';
import { useProgramOfficeData } from '../../hooks/useProgramOfficeData';
import DropDownSelector from './DropDownSelector';
import { ProgramOffice } from '../../services/Types';
import { Alert, Typography } from '@mui/material';

interface DropDownSelectorProgramOfficeProps {
  selectedOffice: ProgramOffice | null;
  onOfficeChange: (office: ProgramOffice | null) => void;
  disabled?: boolean;
}

const DropDownSelectorProgramOffice: React.FC<DropDownSelectorProgramOfficeProps> = ({
  selectedOffice,
  onOfficeChange,
  disabled = false,
}) => {
  const { programOfficeData, errorUseProgramOfficeData, updating, getMyProgramOffices } = useProgramOfficeData();
  const [availableOffices, setAvailableOffices] = useState<ProgramOffice[]>([]);

  const isCurrentSelectionValid = availableOffices.some(item => item.id === selectedOffice?.id);

  useEffect(() => {
    const myProgramOffices = getMyProgramOffices() as ProgramOffice[];
    if (myProgramOffices) {
      setAvailableOffices(myProgramOffices);
    }
  }, [programOfficeData]);

  // Auto-select the single office when not already selected
  useEffect(() => {
    if (availableOffices.length === 1 && !selectedOffice && availableOffices[0]) {
      onOfficeChange(availableOffices[0]);
    }
  }, [availableOffices, selectedOffice, onOfficeChange]);

  // Create the items array for the dropdown
  const dropdownItems = availableOffices.map((office) => ({
    id: office.id || 0,
    name: office.title
  }));

  // Ensure currentId is always a valid option
  const currentId = isCurrentSelectionValid ? (selectedOffice?.id || 0) : 0;

  return (
    <>
      {errorUseProgramOfficeData && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorUseProgramOfficeData}
        </Alert>
      )}
      <DropDownSelector
        label="Program Office"
        currentId={currentId}
        items={dropdownItems}
        disabled={updating || disabled}
        handleCurrentId={(event) => {
          const selectedId = Number(event);
          const office = availableOffices.find(o => o.id === selectedId) || null;
          onOfficeChange(office);
        }}
      />
      {!isCurrentSelectionValid && selectedOffice?.title && (
        <Typography sx={{ color: 'warning.main' }}>
          {selectedOffice?.title} saved but not in this list.
        </Typography>
      )}
    </>
  );
};

export default DropDownSelectorProgramOffice; 